/**
 * Single source for every error surface the web app renders.
 *
 * One entry per error kind; ``ErrorPage`` renders whatever the entry
 * says. Adding a status code means adding a row here — not a new page
 * component. That's what keeps 401/403/404/500/503 from drifting into
 * five hand-rolled screens.
 *
 * ``headingKey`` / ``leadKey`` point at ``common`` namespace keys.
 * Some rows reuse copy that already exists (``notFound.title`` is also
 * rendered by ``Account`` / ``QuoteBatch``; ``errorBoundary.*`` by the
 * root boundary) — pointing at those keys rather than restating them
 * is deliberate, so a wording change lands in one place.
 */

import type { PillTone } from "@/components/ui/Pill";

export type ErrorKind =
  | "stale_version"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "server"
  | "unavailable"
  | "unknown";

export interface ErrorKindSpec {
  /**
   * HTTP status, rendered as the eyebrow. ``null`` for client-side
   * kinds, which label their eyebrow from ``eyebrowKey`` instead —
   * exactly one of the two is set.
   */
  status: number | null;
  eyebrowKey: string | null;
  tone: PillTone;
  headingKey: string;
  leadKey: string;
  /** Which action gets ``variant="primary"``. */
  primary: "reload" | "home";
}

export const ERROR_CATALOG: Record<ErrorKind, ErrorKindSpec> = {
  stale_version: {
    status: null,
    eyebrowKey: "errorPage.staleVersion.eyebrow",
    tone: "warn",
    headingKey: "errorPage.staleVersion.heading",
    leadKey: "errorPage.staleVersion.lead",
    primary: "reload",
  },
  unauthorized: {
    status: 401,
    eyebrowKey: null,
    tone: "neutral",
    headingKey: "errorPage.unauthorized.heading",
    leadKey: "errorPage.unauthorized.lead",
    primary: "home",
  },
  forbidden: {
    status: 403,
    eyebrowKey: null,
    tone: "danger",
    headingKey: "errorPage.forbidden.heading",
    leadKey: "errorPage.forbidden.lead",
    primary: "home",
  },
  not_found: {
    status: 404,
    eyebrowKey: null,
    tone: "neutral",
    headingKey: "notFound.title",
    leadKey: "errorPage.notFound.lead",
    primary: "home",
  },
  server: {
    status: 500,
    eyebrowKey: null,
    tone: "danger",
    headingKey: "errorPage.server.heading",
    leadKey: "errorPage.server.lead",
    primary: "reload",
  },
  unavailable: {
    status: 503,
    eyebrowKey: null,
    tone: "warn",
    headingKey: "errorPage.unavailable.heading",
    leadKey: "errorPage.unavailable.lead",
    primary: "reload",
  },
  unknown: {
    status: null,
    eyebrowKey: "errorPage.unknown.eyebrow",
    tone: "danger",
    headingKey: "errorBoundary.heading",
    leadKey: "errorBoundary.lead",
    primary: "reload",
  },
};

const BY_STATUS: Record<number, ErrorKind> = {
  401: "unauthorized",
  403: "forbidden",
  404: "not_found",
  500: "server",
  503: "unavailable",
};

/** Map an HTTP status onto a catalog row. Unlisted 5xx read as ``server``. */
export function errorKindFromStatus(status: number): ErrorKind {
  return BY_STATUS[status] ?? (status >= 500 ? "server" : "unknown");
}
